/**
 * Session Validation Hook
 * Feature: 007-cloudflare-migration, 008-better-auth-integration
 *
 * Validates session on protected routes and handles expiry.
 * Per FR-012: Session validation on each protected route.
 * 
 * Updated for Better Auth: Uses session tokens instead of JWT.
 */

import { useEffect, useCallback, useRef, useReducer } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { useAuth } from '@/components/auth/AuthProvider'
import {
  hasValidSession,
  getAccessToken,
  checkSessionNeedsRefresh,
  getSession,
} from '@/services/api/auth'

// ============================================================================
// Types
// ============================================================================

export interface UseSessionValidationOptions {
  /** Enable automatic validation on mount and interval (default: true) */
  enabled?: boolean
  /** Validation interval in milliseconds (default: 60000 - 1 minute) */
  interval?: number
  /** Redirect path on session expiry (default: /admin/login) */
  redirectTo?: string
  /** Skip redirect and just return validation state (default: false) */
  skipRedirect?: boolean
}

export interface UseSessionValidationResult {
  /** Whether the session is currently valid */
  isValid: boolean
  /** Whether a validation check is in progress */
  isValidating: boolean
  /** Last validation error, if any */
  error: string | null
  /** Manually trigger a validation check */
  validate: () => Promise<boolean>
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to validate session on protected routes
 *
 * @example
 * // Basic usage - redirects to login if session expires
 * const { isValid, isValidating } = useSessionValidation()
 *
 * @example
 * // Custom options
 * const { validate } = useSessionValidation({
 *   interval: 30000, // Check every 30 seconds
 *   redirectTo: '/login',
 * })
 */
export function useSessionValidation(
  options: UseSessionValidationOptions = {}
): UseSessionValidationResult {
  const {
    enabled = true,
    interval = 60000, // 1 minute
    redirectTo = '/admin/login',
    skipRedirect = false,
  } = options

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('common')
  const { user, signOut } = useAuth()

  // State refs to avoid stale closures
  const isValidRef = useRef(true)
  const isValidatingRef = useRef(false)
  const errorRef = useRef<string | null>(null)
  const mountedRef = useRef(true)

  // Force re-render when state changes
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)
  
  /**
   * Handle session expiry
   */
  const handleSessionExpiry = useCallback(async () => {
    if (skipRedirect) return

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Session expired, redirecting to login',
      level: 'info',
      data: { from: location.pathname },
    })

    // Sign out to clear local state
    await signOut()

    // Navigate with expiry message state
    navigate(redirectTo, {
      replace: true,
      state: {
        sessionExpired: true,
        message: t('api.errors.session_expired'),
        from: location.pathname,
      },
    })
  }, [navigate, redirectTo, location.pathname, signOut, skipRedirect, t])

  /**
   * Validate current session
   */
  const validate = useCallback(async (): Promise<boolean> => {
    if (isValidatingRef.current) {
      return isValidRef.current
    }

    isValidatingRef.current = true
    errorRef.current = null
    forceUpdate()

    try {
      // First check if we have a user at all
      if (!user) {
        isValidRef.current = false
        await handleSessionExpiry()
        return false
      }

      // Quick check using cached session data
      if (!hasValidSession()) {
        isValidRef.current = false
        await handleSessionExpiry()
        return false
      }

      // If session needs refresh, validate with API
      if (checkSessionNeedsRefresh()) {
        try {
          const sessionResponse = await getSession()
          if (!sessionResponse?.session || !sessionResponse?.user) {
            isValidRef.current = false
            await handleSessionExpiry()
            return false
          }
        } catch {
          // Session refresh failed
          isValidRef.current = false
          await handleSessionExpiry()
          return false
        }
      }

      // Verify token can be retrieved
      const token = await getAccessToken()
      if (!token) {
        isValidRef.current = false
        await handleSessionExpiry()
        return false
      }

      isValidRef.current = true
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Session validation failed'
      errorRef.current = errorMessage
      isValidRef.current = false

      Sentry.captureException(err, {
        tags: { feature: 'session-validation' },
      })

      await handleSessionExpiry()
      return false
    } finally {
      if (mountedRef.current) {
        isValidatingRef.current = false
        forceUpdate()
      }
    }
  }, [user, handleSessionExpiry])

  // Validate on mount
  useEffect(() => {
    mountedRef.current = true

    if (enabled && user) {
      validate()
    }

    return () => {
      mountedRef.current = false
    }
  }, [enabled, user, validate])

  // Periodic validation
  useEffect(() => {
    if (!enabled || !user || interval <= 0) {
      return
    }

    const intervalId = setInterval(() => {
      validate()
    }, interval)

    return () => {
      clearInterval(intervalId)
    }
  }, [enabled, user, interval, validate])

  return {
    isValid: isValidRef.current,
    isValidating: isValidatingRef.current,
    error: errorRef.current,
    validate,
  }
}

export default useSessionValidation
